import React from 'react';
import { Check, MessageSquarePlus, Send, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

import { useSubmitAdminFeedbackMutation } from '../graphql/mutations/submitAdminFeedback.generated';

/**
 * Master on/off switch for every feedback banner. Flip to `false` (one line,
 * one deploy) once a rollout has run long enough — the component then renders
 * nothing everywhere it's used.
 */
export const FEEDBACK_ENABLED = true;

const dismissKey = (path: string) => `admin-feedback-dismissed:${path}`;

type FeedbackBannerProps = {
  /** Screen identifier stored with the feedback, e.g. "edit-container-types". */
  path: string;
  /** Optional overrides — default to the generic Feedback:* strings. */
  title?: string;
  description?: string;
  className?: string;
};

/**
 * Reusable "tell us what you think" banner for newly released screens. The
 * banner itself is a compact prompt + button; the free-text field lives in a
 * dialog. Free text → colugo `submitAdminFeedback` → ClickHouse. shadcn/tw
 * only, responsive. Users can dismiss it per screen (persisted in
 * localStorage); the whole feature is killed via FEEDBACK_ENABLED.
 */
export default function FeedbackBanner({
  path,
  title,
  description,
  className,
}: FeedbackBannerProps) {
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(dismissKey(path)) === '1';
    } catch {
      return false;
    }
  });
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitFeedback, { loading }] = useSubmitAdminFeedbackMutation();

  if (!FEEDBACK_ENABLED || dismissed) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(dismissKey(path), '1');
    } catch {
      /* ignore storage failures — still hide for this session */
    }
    setDismissed(true);
  };

  const send = async () => {
    const trimmed = comment.trim();
    if (trimmed === '' || loading) return;
    try {
      await submitFeedback({ variables: { path, comment: trimmed } });
      setSubmitted(true);
      setDialogOpen(false);
      setComment('');
    } catch {
      toast.error(i18n.t('Feedback:error'));
    }
  };

  const infoColor = 'var(--color-info-main, oklch(0.62 0.13 233))';

  return (
    <>
      <div
        role="alert"
        className={cn(
          'tw:relative tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-l-4 tw:px-4 tw:py-3 tw:sm:flex-row tw:sm:items-center tw:sm:gap-4 tw:sm:pr-12',
          className,
        )}
        style={{
          backgroundColor: `color-mix(in oklab, ${infoColor} 8%, var(--background))`,
          borderColor: `color-mix(in oklab, ${infoColor} 30%, transparent)`,
          borderLeftColor: infoColor,
        }}
      >
        {/* Dismiss — pinned top-right so it never competes for row space. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label={i18n.t('Feedback:dismiss')}
          title={i18n.t('Feedback:dismiss')}
          className="tw:absolute tw:right-2 tw:top-2 tw:flex tw:size-7 tw:flex-none tw:items-center tw:justify-center tw:rounded-md tw:text-muted-foreground tw:outline-none tw:cursor-pointer tw:hover:bg-accent tw:hover:text-foreground"
        >
          <X size={16} />
        </button>

        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-3 tw:pr-7 tw:sm:pr-0">
          <MessageSquarePlus
            size={18}
            className="tw:mt-0.5 tw:flex-none"
            style={{ color: infoColor }}
          />
          <div className="tw:min-w-0">
            <div className="tw:text-sm tw:font-semibold tw:text-foreground">
              {submitted
                ? i18n.t('Feedback:thanksTitle')
                : (title ?? i18n.t('Feedback:title'))}
            </div>
            <div className="tw:text-sm tw:text-muted-foreground">
              {submitted
                ? i18n.t('Feedback:thanksText')
                : (description ?? i18n.t('Feedback:description'))}
            </div>
          </div>
        </div>

        {submitted ? (
          <span
            className="tw:inline-flex tw:flex-none tw:items-center tw:gap-1.5 tw:text-sm tw:font-medium"
            style={{ color: 'var(--color-success-main)' }}
          >
            <Check size={15} />
            {i18n.t('Feedback:submitted')}
          </span>
        ) : (
          <Button
            className="tw:w-full tw:flex-none tw:sm:w-auto"
            onClick={() => setDialogOpen(true)}
          >
            <MessageSquarePlus size={15} />
            {i18n.t('Feedback:give')}
          </Button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="tw:sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{i18n.t('Feedback:dialogTitle')}</DialogTitle>
            <DialogDescription>
              {description ?? i18n.t('Feedback:description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={i18n.t('Feedback:placeholder')}
              rows={5}
              onKeyDown={(e) => {
                // Cmd/Ctrl+Enter submits, like most comment boxes.
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  send();
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {i18n.t('Common:cancel')}
            </Button>
            <Button onClick={send} disabled={comment.trim() === '' || loading}>
              {loading ? (
                i18n.t('Feedback:sending')
              ) : (
                <>
                  <Send size={15} />
                  {i18n.t('Feedback:send')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
